import {
  keepPreviousData,
  QueryKey,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { translate as t } from '../../../config/i18n.config';
import queryClient from '../../../config/tanstackQueryConfig';
import { handleErrors } from '../../../utils';
import OrganizationsApi from './organizations.api';

import { ActionResponse } from '../../../types';
import {
  OrganizationFormInterface,
  OrganizationInterface,
  OrganizationsResponseDataInterface,
} from './organizations.types';

const QUERY_KEY = 'organizations';

const api: OrganizationsApi = new OrganizationsApi();

class OrganizationsStore {
  preloadOrganizations = async ({
    page,
    searchTerm,
    enabled = true,
  }: {
    page?: number;
    searchTerm?: string;
    enabled?: boolean;
  }) => {
    return await queryClient.fetchQuery(
      organizationsQueryOptions({ page, searchTerm, enabled })
    );
  };

  useGetOrganizations = ({
    page,
    searchTerm,
    enabled = true,
  }: {
    page?: number;
    searchTerm?: string;
    enabled?: boolean | null;
  }) => {
    return useQuery(organizationsQueryOptions({ page, searchTerm, enabled }));
  };

  getOrganization = async (
    id: number
  ): Promise<ActionResponse<OrganizationInterface>> => {
    try {
      const res = await api.getOrganization(id);
      const resData = res.data;
      return {
        success: true,
        message:
          resData?.message ||
          t('institutions.institution_successfully_fetched'),
        data: resData,
      };
    } catch (error) {
      const { message, success } = handleErrors(error);
      return { message, success };
    }
  };

  preloadOrganization = this.getOrganization;

  addOrganization = async (
    data: OrganizationFormInterface
  ): Promise<ActionResponse<OrganizationInterface>> => {
    try {
      const res = await api.addOrganization(data);
      const resData = res.data;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      return {
        success: true,
        message:
          resData?.message || t('institutions.institution_successfully_added'),
        data: resData,
      };
    } catch (error) {
      const { message, success } = handleErrors(error);
      return { message, success };
    }
  };

  // Update organization
  updateOrganization = async (
    id: number,
    data: OrganizationFormInterface
  ): Promise<ActionResponse<OrganizationInterface>> => {
    try {
      const res = await api.updateOrganization(id, data);
      const resData = res.data;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      return {
        success: true,
        message:
          resData?.message ||
          t('institutions.institution_successfully_updated'),
        data: resData,
      };
    } catch (error) {
      const { message, success } = handleErrors(error);
      return { message, success };
    }
  };

  // Delete Organization
  deleteOrganization = async (
    id: number
  ): Promise<ActionResponse<OrganizationInterface>> => {
    try {
      const res = await api.deleteOrganization(id);
      const resData = res.data;
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      return {
        success: true,
        message:
          resData?.message ||
          t('institutions.institution_successfully_deleted'),
        data: resData,
      };
    } catch (error) {
      const { message, success } = handleErrors(error);
      return { message, success };
    }
  };

  getOrganizationLogo = async (id: number) => {
    try {
      const res: any = await api.getOrganizationLogo(id);
      const imageBlob = new Blob([res.data], {
        type: res.headers['content-type'],
      });
      const url = URL.createObjectURL(imageBlob);
      return {
        success: true,
        message: res.data?.message || t('institutions.logo_successfully_added'),
        data: url,
      };
    } catch (error) {
      const { message, success } = handleErrors(error);
      return { message, success };
    }
  };
}

/**
 * Returns query options for fetching / prefetching organizations.
 */
export const organizationsQueryOptions = ({
  page = 1,
  searchTerm,
  enabled = true,
}: {
  page?: number;
  searchTerm?: string;
  enabled?: boolean | null;
}): UseQueryOptions<OrganizationsResponseDataInterface, Error> => ({
  queryKey: ['organizations', { page, searchTerm }] as QueryKey,
  queryFn: async (): Promise<OrganizationsResponseDataInterface> => {
    const response: AxiosResponse<OrganizationsResponseDataInterface> =
      await api.getOrganizations(page, searchTerm);
    return response.data;
  },
  enabled: enabled ? true : false,
  placeholderData: keepPreviousData,
});

export default OrganizationsStore;
